import {
  compact,
  filter,
  flow,
  get,
  getOr,
  includes,
  isEmpty,
  isNil,
  join,
  map,
  negate,
  reduce,
  sortBy,
} from 'lodash/fp';
import {createSelector} from 'reselect';

import {RANK_ICON_NAME} from '/component/base/RankIcon/config';
import {HealthcareApiData} from '/component/screen/Profile/ProfileBusinessSection/ProfileBusinessSectionContainer/ProfileBusinessSectionContainer.types';
import {
  BUSINESS_COACH,
  BUSINESS_LEADER,
  COACH,
  RAW_BUSINESS_COACH,
  RAW_BUSINESS_LEADER,
  RAW_COACH,
} from '/enum/competency.enum';
import {LABEL_COMING_SOON} from '/enum/label.enum';
import {RANK_BY_ID} from '/enum/rank.enum';
import {hasAchievedHighestRank} from '/util/rank.util';
import {fromProps} from '/util/selector.util';

import {selectHealthCareProviderFields} from './remoteConfig.selector';
import {coach as coachState} from './root.selector';

export const coachCompetencies = createSelector(
  coachState,
  get('coachCompetencies'),
);

export const rankProgressions = createSelector(
  coachState,
  get('rankProgressions'),
);
export const sameDayLastMonth = createSelector(
  coachState,
  get('sameDayLastMonth'),
);
export const sameDayLastMonthById = createSelector(
  fromProps(get('coachId')),
  sameDayLastMonth,
  (coachId, sameDayLastMonth) => get(coachId)(sameDayLastMonth),
);
export const hasSameDayLastMonthById = createSelector(
  sameDayLastMonthById,
  negate(isEmpty),
);
export const CABBanner = createSelector(coachState, get('CABBanner'));

export const acceleratorBonusBanner = createSelector(
  coachState,
  get('acceleratorBonusBanner'),
);

export const CSBPlusBanner = createSelector(coachState, get('CSBPlusBanner'));

export const rankProgressionDetailVisibilities = createSelector(
  coachState,
  get('rankProgressionDetailVisibilities'),
);

export const allProfiles = createSelector(coachState, get('allProfiles'));

export const allPerformances = createSelector(
  coachState,
  get('allPerformances'),
);

export const allEarnings = createSelector(coachState, get('allEarnings'));

export const allTeams = createSelector(coachState, get('allTeams'));

export const competencyById = createSelector(
  fromProps(get('coachId')),
  coachCompetencies,
  (coachId, competencies) => {
    const rawCompetency = get(coachId)(competencies);

    if (rawCompetency === RAW_COACH) {
      return COACH;
    } else if (rawCompetency === RAW_BUSINESS_COACH) {
      return BUSINESS_COACH;
    } else if (rawCompetency === RAW_BUSINESS_LEADER) {
      return BUSINESS_LEADER;
    } else {
      return COACH;
    }
  },
);

export const profileById = createSelector(
  fromProps(get('coachId')),
  allProfiles,
  (coachId, profiles) => get(coachId)(profiles),
);

export const cabBannerById = createSelector(
  fromProps(get('coachId')),
  CABBanner,
  (coachId, cabBanner) => get(coachId)(cabBanner),
);

export const acceleratorBonusBannerById = createSelector(
  fromProps(get('coachId')),
  acceleratorBonusBanner,
  (coachId, cabBanner) => get(coachId)(cabBanner),
);

export const CSBPlusBannerById = createSelector(
  fromProps(get('coachId')),
  CSBPlusBanner,
  (coachId, CsbPlusBanner) => get(coachId)(CsbPlusBanner),
);

export const hasProfileById = createSelector(profileById, negate(isNil));

export const avatarUrlById = createSelector(profileById, get('avatarUrl'));

export const accountEmailById = createSelector(
  profileById,
  get('accountEmail'),
);

export const phoneNumberById = createSelector(profileById, get('phoneNumber'));

export const isCertifiedById = createSelector(profileById, get('certified'));

export const licenseExpiresOnById = createSelector(
  profileById,
  get('licenseExpiresOn'),
);

export const firstNameById = createSelector(profileById, get('firstName'));

export const activationDateById = createSelector(
  profileById,
  get('activatedOn'),
);

export const lastNameById = createSelector(profileById, get('lastName'));

export const fullNameById = createSelector(
  firstNameById,
  lastNameById,
  (firstName, lastName) => flow([compact, join(' ')])([firstName, lastName]),
);

export const addressById = createSelector(profileById, get('address'));

export const addressCityById = createSelector(addressById, get('city'));

export const addressStateById = createSelector(addressById, get('state'));

export const addressCityStateById = createSelector(
  addressCityById,
  addressStateById,
  (city, state) => {
    return flow([compact, join(', ')])([city, state]);
  },
);

export const businessCoachById = createSelector(
  profileById,
  get('businessCoach'),
);

export const businessCoachIdById = createSelector(businessCoachById, get('id'));

export const businessCoachFirstNameById = createSelector(
  businessCoachById,
  get('firstName'),
);

export const businessCoachLastNameById = createSelector(
  businessCoachById,
  get('lastName'),
);

export const businessCoachFullNameById = createSelector(
  businessCoachFirstNameById,
  businessCoachLastNameById,
  (firstName, lastName) => flow([compact, join(' ')])([firstName, lastName]),
);

export const rankIdById = createSelector(profileById, get('rank.id'));

export const rankDescriptionById = createSelector(
  profileById,
  get('rank.description'),
);

export const rankIconNameById = createSelector(rankIdById, rankId => {
  // @ts-expect-error TS(2538): Type 'unknown' cannot be used as an index type.
  const rankName = RANK_BY_ID[rankId];
  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  return RANK_ICON_NAME[rankName];
});

export const highestAchievedRankById = createSelector(
  profileById,
  get('highestAchievedRank'),
);

export const highestAchievedRankIdById = createSelector(
  highestAchievedRankById,
  get('id'),
);

export const highestAchievedRankDescriptionBy_Id = createSelector(
  highestAchievedRankById,
  get('description'),
);

export const highestAchievedRankDescriptionById = createSelector(
  highestAchievedRankDescriptionBy_Id,
  (rank: string | null | undefined) => (rank ? rank : 'na'),
);

export const nextRankById = createSelector(profileById, get('nextRank'));

export const hasNextRankById = createSelector(
  rankIdById,
  rankId => !hasAchievedHighestRank(rankId),
);

export const nextRankIdById = createSelector(nextRankById, get('id'));

export const nextRankDescriptionById = createSelector(
  profileById,
  get('nextRank.description'),
);

export const paidAsRankDescLast_Month = createSelector(
  profileById,
  get('paidAsRankDescLastMonth'),
);

export const paidAsRankDescLastMonth = createSelector(
  paidAsRankDescLast_Month,
  (rank: string | null | undefined) => (rank ? rank : 'na'),
);

export const nextRankProgressById = createSelector(
  profileById,
  flow([
    get('percentProgressToNextRank'),
    (percent: any) => parseFloat(percent),
  ]),
);

export const urlDetailsById = createSelector(profileById, get('urlDetails'));

export const nextScheduledPremierOrderById = createSelector(
  profileById,
  get('nextScheduledPremierOrder'),
);

export const hasScheduledPremierOrderById = createSelector(
  nextScheduledPremierOrderById,
  negate(isEmpty),
);

export const nextPremierOrderScheduledOnById = createSelector(
  nextScheduledPremierOrderById,
  get('scheduledOn'),
);

export const nextPremierOrderCvById = createSelector(
  nextScheduledPremierOrderById,
  get('cv'),
);

export const nextPremierOrderQvById = createSelector(
  nextScheduledPremierOrderById,
  get('qv'),
);

export const nextPremierOrderTotalById = createSelector(
  nextScheduledPremierOrderById,
  flow([get('price.total'), parseFloat]),
);

export const nextPremierOrderCurrencyCodeById = createSelector(
  nextScheduledPremierOrderById,
  get('price.currencyCode'),
);

export const wellnessCreditsById = createSelector(
  profileById,
  get('wellnessCredits'),
);

export const wellnessCreditsBalanceById = createSelector(
  wellnessCreditsById,
  flow([get('balance'), parseInt]),
);

export const wellnessCreditsCurrencyCodeById = createSelector(
  wellnessCreditsById,
  get('currencyCode'),
);

export const rewardsById = createSelector(profileById, get('expiringRewards'));

export const rewardsBalanceById = createSelector(
  rewardsById,
  flow([get('balance'), parseFloat]),
);

export const rewardsCurrencyCodeById = createSelector(
  rewardsById,
  get('currencyCode'),
);

export const rankProgressionByCoachAndRankIds = createSelector(
  fromProps(get('coachId')),
  fromProps(get('rankId')),
  rankProgressions,
  (coachId, rankId, progressions) => {
    return get(`${coachId}->${rankId}`)(progressions);
  },
);

export const activationDate = createSelector(
  rankProgressionByCoachAndRankIds,
  get('activationDate'),
);

export const hasRankProgressionByCoachAndRankIds = createSelector(
  rankProgressionByCoachAndRankIds,
  negate(isEmpty),
);

export const rankProgressionDetailVisibleByCoachAndRankIds = createSelector(
  fromProps(get('coachId')),
  fromProps(get('rankId')),
  fromProps(get('targetName')),
  rankProgressionDetailVisibilities,
  (coachId, rankId, targetName, visibilities) => {
    return getOr(false, `${coachId}->${rankId}.${targetName}`)(visibilities);
  },
);

export const performanceById = createSelector(
  fromProps(get('coachId')),
  allPerformances,
  (coachId, performances) => {
    return get(coachId)(performances);
  },
);

export const hasPerformanceById = createSelector(
  performanceById,
  negate(isEmpty),
);

export const performanceNewFrontlineClientsCountById = createSelector(
  performanceById,
  flow([getOr(0, 'newFrontlineClientsThisMonthCount'), parseInt]),
);

export const performanceNewGen0ClientsCountById = createSelector(
  performanceById,
  flow([getOr(0, 'newGen0ClientsThisMonthCount'), parseInt]),
);

export const performanceNewOrgClientsCountById = createSelector(
  performanceById,
  flow([getOr(0, 'newOrgClientsThisMonthCount'), parseInt]),
);

export const performanceNewFrontlineCoachesCountById = createSelector(
  performanceById,
  flow([getOr(0, 'newFrontlineCoachesThisMonthCount'), parseInt]),
);

export const performanceNewGen0CoachesCountById = createSelector(
  performanceById,
  flow([getOr(0, 'newGen0CoachesThisMonthCount'), parseInt]),
);

export const performanceNewOrgCoachesCountById = createSelector(
  performanceById,
  flow([getOr(0, 'newOrgCoachesThisMonthCount'), parseInt]),
);

export const performanceOrderingEntitiesCountById = createSelector(
  performanceById,
  flow([getOr(0, 'orderingEntitiesCount'), parseInt]),
);

export const performanceQualifyingPointsById = createSelector(
  performanceById,
  flow([getOr(0, 'qualifyingPoints'), parseInt]),
);

export const performanceCurrentPremierVolumeById = createSelector(
  performanceById,
  getOr(0, 'currentPremierVolume'),
);

export const performanceProjectedPremierVolumeById = createSelector(
  performanceById,
  getOr(0, 'projectedPremierVolume'),
);

export const performanceFrontlineReactivatedClientsCountById = createSelector(
  performanceById,
  flow([getOr(0, 'frontlineReactivatedClientsCount'), parseInt]),
);

export const performanceGen0ReactivatedClientsCountById = createSelector(
  performanceById,
  flow([getOr(0, 'gen0ReactivatedClientsCount'), parseInt]),
);
export const performanceProjectedTotalVolumeById = createSelector(
  performanceCurrentPremierVolumeById,
  performanceProjectedPremierVolumeById,
  (current, projected) => {
    if (current === LABEL_COMING_SOON || projected === LABEL_COMING_SOON) {
      return LABEL_COMING_SOON;
    }
    // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
    return parseInt(current, 10) + parseInt(projected, 10);
  },
);

export const performanceCurrentFrontlineVolumeById = createSelector(
  performanceById,
  getOr(0, 'frontline.volumeThisMonth'),
);

export const performanceSameDayLastMonthFrontlineVolumeById = createSelector(
  sameDayLastMonthById,
  get('frontlineVol'),
);

export const performanceDeltaFrontlineVolumeById = createSelector(
  performanceCurrentFrontlineVolumeById,
  performanceSameDayLastMonthFrontlineVolumeById,
  (thisMonth, lastMonth) => {
    // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
    thisMonth = parseInt(thisMonth, 10);
    // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
    lastMonth = parseInt(lastMonth, 10);
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const diff = Math.abs(thisMonth - lastMonth);
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const delta = (diff / lastMonth) * 100 * (thisMonth < lastMonth ? -1 : 1);
    // @ts-expect-error TS(2345): Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
    return parseInt(delta * 10, 10) / 10;
  },
);

export const performanceCurrentGen0VolumeById = createSelector(
  performanceById,
  getOr(0, 'gen0.volumeThisMonth'),
);

export const performanceSameDayLastMonthGen0VolumeById = createSelector(
  sameDayLastMonthById,
  get('gen0Vol'),
);

export const performanceDeltaGen0VolumeById = createSelector(
  performanceCurrentGen0VolumeById,
  performanceSameDayLastMonthGen0VolumeById,
  (thisMonth, lastMonth) => {
    // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
    thisMonth = parseInt(thisMonth, 10);
    // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
    lastMonth = parseInt(lastMonth, 10);
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const diff = Math.abs(thisMonth - lastMonth);
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const delta = (diff / lastMonth) * 100 * (thisMonth < lastMonth ? -1 : 1);
    // @ts-expect-error TS(2345): Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
    return parseInt(delta * 10, 10) / 10;
  },
);

export const performanceCurrentTotalVolumeById = createSelector(
  performanceById,
  getOr(0, 'totalGeneration.volumeThisMonth'),
);

export const performanceSameDayLastMonthTotalVolumeById = createSelector(
  sameDayLastMonthById,
  get('totalVol'),
);

export const performanceDeltaTotalVolumeById = createSelector(
  performanceCurrentTotalVolumeById,
  performanceSameDayLastMonthTotalVolumeById,
  (thisMonth, lastMonth) => {
    // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
    thisMonth = parseInt(thisMonth, 10);
    // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
    lastMonth = parseInt(lastMonth, 10);
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const diff = Math.abs(thisMonth - lastMonth);
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const delta = (diff / lastMonth) * 100 * (thisMonth < lastMonth ? -1 : 1);
    // @ts-expect-error TS(2345): Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
    return parseInt(delta * 10, 10) / 10;
  },
);

export const earningsById = createSelector(
  fromProps(get('coachId')),
  allEarnings,
  (coachId, earnings) => {
    return get(coachId)(earnings);
  },
);

export const hasEarningsById = createSelector(earningsById, negate(isEmpty));

export const teamsById = createSelector(
  fromProps(get('coachId')),
  allTeams,
  (coachId, teams) => {
    return get(coachId)(teams);
  },
);

export const hasTeamsById = createSelector(teamsById, negate(isEmpty));

export const teamsSeniorCoachCountById = createSelector(
  teamsById,
  getOr(0, 'seniorCoachTeamsCount'),
);

export const teamsExecutiveDirectorCountById = createSelector(
  teamsById,
  getOr(0, 'executiveDirectorTeamsCount'),
);

export const teamsFibcCountById = createSelector(
  teamsById,
  getOr(0, 'fibcTeamsCount'),
);

export const teamsFiblCountById = createSelector(
  teamsById,
  getOr(0, 'fiblTeamsCount'),
);

const MENTOR_POSITIONS = [
  'businessCoach',
  'globalDirector',
  'presidentialDirector',
];

const MENTOR_SORT_ORDER = {
  businessCoach: 0,
  globalDirector: 1,
  presidentialDirector: 2,
};

export const teamsMentorsById = createSelector(teamsById, teams => {
  return flow([
    reduce(
      (result: any, position: any) => [
        ...result,
        {
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          sortOrder: MENTOR_SORT_ORDER[position],
          mentor: {position, coach: teams && teams[position]},
        },
      ],
      [],
    ),
    sortBy(get('sortOrder')),
    map(get('mentor')),
  ])(MENTOR_POSITIONS);
});

export const teamsHasMentorsById = createSelector(
  teamsMentorsById,
  flow([
    filter(
      ({position, coach}: any) =>
        !isEmpty(coach) && includes(position)(MENTOR_POSITIONS),
    ),
    negate(isEmpty),
  ]),
);

export const cabActivationDate = createSelector(
  cabBannerById,
  get('activatedOn'),
);

export const cabFqv = createSelector(cabBannerById, get('fqvFirst30Days'));

export const cabEligibleThroughDate = createSelector(
  cabBannerById,
  get('eligibleForCABThrough'),
);

export const orderingEntityFirst30Days = createSelector(
  cabBannerById,
  get('orderingEntityFirst30Days'),
);

export const acceleratorBannerEndDate = createSelector(
  acceleratorBonusBannerById,
  get('eligibilityEndDate'),
);

export const acceleratorBannerTotalOECount = createSelector(
  acceleratorBonusBannerById,
  get('totalOECount'),
);

export const acceleratorBannerCurrentDate = createSelector(
  acceleratorBonusBannerById,
  get('currentDate'),
);

export const acceleratorBannerStartDate = createSelector(
  acceleratorBonusBannerById,
  bannerData =>
    getOr(
      getOr('', 'eligibilityStartDate', bannerData),
      'activatedOn',
      bannerData,
    ),
);

export const CSBPlusBannerEndDate = createSelector(
  CSBPlusBannerById,
  get('eligibilityEndDate'),
);

export const totalOECount = createSelector(
  CSBPlusBannerById,
  get('totalOECount'),
);

export const newOECount = createSelector(CSBPlusBannerById, get('newOECount'));

export const reactivatedOECount = createSelector(
  CSBPlusBannerById,
  get('reactivatedOECount'),
);

export const CSBPlusBannerOE = createSelector(
  CSBPlusBannerById,
  get('newOrderingEntityCount'),
);

export const CSBPlusBannerCurrentDate = createSelector(
  CSBPlusBannerById,
  get('currentDate'),
);

export const bonuses = createSelector(coachState, get('bonuses'));

export const incentiveTripBonusBanner = createSelector(
  coachState,
  get('incentiveTripBonusBanner'),
);

export const incentiveTripBonusBannerById = createSelector(
  fromProps(get('coachId')),
  incentiveTripBonusBanner,
  (coachId, incentiveTripBanner) => get(coachId)(incentiveTripBanner),
);

export const hasIncentiveTripBonusBanner = createSelector(
  incentiveTripBonusBanner,
  negate(isEmpty),
);

export const incentiveTripBannerStartDate = createSelector(
  incentiveTripBonusBannerById,
  get('incentiveStartDate'),
);

export const incentiveTripBannerEndDate = createSelector(
  incentiveTripBonusBannerById,
  get('incentiveEndDate'),
);

export const incentiveTripBannerCurrentDate = createSelector(
  incentiveTripBonusBannerById,
  get('currentDate'),
);

export const incentiveTripBannerPath = createSelector(
  incentiveTripBonusBannerById,
  get('path'),
);

export const incentiveTripBannerCoachGoalRank = createSelector(
  incentiveTripBonusBannerById,
  get('goalRank'),
);

export const incentiveTripBannerOE = createSelector(
  incentiveTripBonusBannerById,
  get('orderingEntities'),
);

export const incentiveTripBannerRankAchievements = createSelector(
  incentiveTripBonusBannerById,
  get('rankAchievements'),
);

export const incentiveTripBannerFQV = createSelector(
  incentiveTripBonusBannerById,
  get('fqv'),
);

export const incentiveTripBannerNewCoachCount = createSelector(
  incentiveTripBonusBannerById,
  get('newCoachCount'),
);

export const incentiveTripBannerSupportPathCount = createSelector(
  incentiveTripBonusBannerById,
  get('supportPathCount'),
);
export const notesById = createSelector(profileById, get('notes'));

export const notesUpdatedTsById = createSelector(
  profileById,
  get('notesUpdatedTs'),
);
export const allNotesState = createSelector(coachState, get('allNotesState'));

export const BBBBanner = createSelector(coachState, get('BBBBanner'));

export const businessBuilderBonusBanner = createSelector(
  coachState,
  get('BBBBanner'),
);

export const businessBuilderBonusBannerById = createSelector(
  fromProps(get('coachId')),
  businessBuilderBonusBanner,
  (coachId, businessBuilderBanner) => get(coachId)(businessBuilderBanner),
);

export const hasBusinessBuilderBonusBanner = createSelector(
  businessBuilderBonusBannerById,
  negate(isEmpty),
);

export const businessBuilderBonusFQV = createSelector(
  businessBuilderBonusBannerById,
  get('fqv'),
);

export const businessBuilderBonusCoachesCount = createSelector(
  businessBuilderBonusBannerById,
  get('businessBuilderCoachesCount'),
);

export const businessBuilderBonusCurrentDate = createSelector(
  businessBuilderBonusBannerById,
  get('currentDate'),
);

export const businessBuilderBonusContributors = createSelector(
  coachState,
  get('BBBContributors'),
);

export const businessBuilderBonusContributorsById = createSelector(
  fromProps(get('coachId')),
  businessBuilderBonusContributors,
  (coachId, bonusContributors) => get(coachId)(bonusContributors),
);

export const hasBBBContributorsById = createSelector(
  businessBuilderBonusContributorsById,
  negate(isEmpty),
);

export const BBBonusContributors = createSelector(
  businessBuilderBonusContributorsById,
  get('bonusContributors'),
);
export const BBBonusContributorActivationDate = createSelector(
  businessBuilderBonusContributorsById,
  get('activatedOn'),
);

export const BBBonusContributorCurrentDate = createSelector(
  businessBuilderBonusContributorsById,
  get('currentDate'),
);

export const edge365ChallengeTasks = createSelector(coachState, get('edge365'));

export const edge365ChallengeTasksById = createSelector(
  (state, props) => props.coachId,
  edge365ChallengeTasks,
  (coachId, edge365ChallengeTasks) => edge365ChallengeTasks?.[coachId] ?? {},
);

export const highestStreak = createSelector(
  edge365ChallengeTasksById,
  get('highestStreak'),
);
export const coachTasks = createSelector(
  edge365ChallengeTasksById,
  get('coachTask'),
);
export const latestStreak = createSelector(
  edge365ChallengeTasksById,
  get('latestStreak'),
);

export const hasEdge365ChallengeTasksById = createSelector(
  edge365ChallengeTasksById,
  negate(isEmpty),
);

export const businessProfile = createSelector(
  coachState,
  get('businessProfile.data'),
);

export const isBusinessProfileLoading = createSelector(
  coachState,
  get('businessProfile.isLoading'),
);

export const isBusinessProfileError = createSelector(
  coachState,
  get('businessProfile.hasError'),
);

export const isBusinessProfileSaving = createSelector(
  coachState,
  get('businessProfile.isSaving'),
);

export const hasBusinessProfile = createSelector(
  businessProfile,
  profile => !isNil(profile) && !isEmpty(profile),
);

// export const socialMedia = createSelector(coachState, state =>
//   Object.values(state.socialMedia?.data ?? {}),
// );

export const socialMedia = createSelector(coachState, state =>
  Object.values(state.socialMedia?.data ?? {})
    .reduce((acc: any[], value: any) => acc.concat(value), [])
    .map((item: any) => ({
      ...item,
      type: item?.type?.toLowerCase().includes('blog')
        ? 'Blog / Website'
        : item?.type,
    })),
);

export const isSocialMediaLoading = createSelector(
  coachState,
  get('socialMedia.isLoading'),
);

export const isSocialMediaSaving = createSelector(
  coachState,
  get('socialMedia.isSaving'),
);

export const isSocialMediaError = createSelector(
  coachState,
  get('socialMedia.hasError'),
);

export const hasSocialMedia = createSelector(
  socialMedia,
  links => !isNil(links) && !isEmpty(links),
);

export const leadsPoolState = createSelector(coachState, get('leadsPool'));

export const leadsPoolData = createSelector(leadsPoolState, get('data'));

export const leadsPoolOptIn = createSelector(
  leadsPoolData,
  get('leadsPoolOptIn'),
);

export const hasLeadsPool = createSelector(
  leadsPoolData,
  data => !isNil(data) && !isEmpty(data),
);

export const isLeadsPoolLoading = createSelector(
  leadsPoolState,
  get('isLoading'),
);

export const isLeadsPoolSaving = createSelector(
  leadsPoolState,
  get('isSaving'),
);

export const hasLeadsPoolError = createSelector(
  leadsPoolState,
  get('hasError'),
);

export const healthcareState = createSelector(coachState, get('healthcare'));

export const healthcareData = createSelector(
  healthcareState,
  selectHealthCareProviderFields,
  (state, registeredFieldOptions) => {
    const data = state?.data as HealthcareApiData | undefined;
    if (!data) {
      return null;
    }
    const isPredefinedField = registeredFieldOptions.some(
      option => option.value === data.field,
    );
    const result = {
      isHealthcareProvider: data.healthCareProvider ?? null,
      registeredField: isPredefinedField ? data.field ?? '' : 'other',
      registeredState: data.stateProvince ?? '',
      licenseNumber: data.licenseNumber ?? '',
      otherRegisteredField: isPredefinedField ? '' : data.field ?? '',
    };
    return result;
  },
);

export const healthcareProvider = createSelector(
  healthcareData,
  get('healthcareProvider'),
);

export const healthcareRegisteredField = createSelector(
  healthcareData,
  get('registeredField'),
);

export const healthcareRegisteredState = createSelector(
  healthcareData,
  get('registeredState'),
);

export const healthcareLicenseNumber = createSelector(
  healthcareData,
  get('licenseNumber'),
);

export const healthcareOtherRegisteredField = createSelector(
  healthcareData,
  get('otherRegisteredField'),
);

export const hasHealthcareData = createSelector(healthcareState, state => {
  const data = state?.data;
  return !isNil(data) && !isEmpty(data) && Object.keys(data).length > 0;
});

export const isHealthcareLoading = createSelector(
  healthcareState,
  get('isLoading'),
);

export const isHealthcareSaving = createSelector(
  healthcareState,
  get('isSaving'),
);

export const hasHealthcareError = createSelector(
  healthcareState,
  get('hasError'),
);

export const supportedLanguagesState = createSelector(
  coachState,
  get('supportedLanguages'),
);

export const supportedLanguagesData = createSelector(
  supportedLanguagesState,
  get('data'),
);

export const supportedLanguagesList = supportedLanguagesData;

export const supportedLanguages = createSelector(
  supportedLanguagesData,
  languageList => {
    if (!Array.isArray(languageList)) return [];
    return languageList
      .filter(item => item.isSupported)
      .map(item => item.language.toLowerCase());
  },
);

export const hasSupportedLanguages = createSelector(
  supportedLanguagesData,
  data => Array.isArray(data) && data.length > 0,
);

export const isSupportedLanguagesLoading = createSelector(
  supportedLanguagesState,
  get('isLoading'),
);

export const isSupportedLanguagesSaving = createSelector(
  supportedLanguagesState,
  get('isSaving'),
);

export const hasSupportedLanguagesError = createSelector(
  supportedLanguagesState,
  get('hasError'),
);

export const businessProfileImageState = createSelector(
  coachState,
  get('businessProfileImage'),
);

export const avatarBusinessProfileImageState = createSelector(
  businessProfileImageState,
  get('avatar'),
);

export const beforeBusinessProfileImageState = createSelector(
  businessProfileImageState,
  get('before'),
);

export const afterBusinessProfileImageState = createSelector(
  businessProfileImageState,
  get('after'),
);

export const avatarBusinessProfileImageUrl = createSelector(
  avatarBusinessProfileImageState,
  get('url'),
);

export const beforeBusinessProfileImageUrl = createSelector(
  beforeBusinessProfileImageState,
  get('url'),
);

export const afterBusinessProfileImageUrl = createSelector(
  afterBusinessProfileImageState,
  get('url'),
);

export const isAvatarBusinessProfileImageSaving = createSelector(
  avatarBusinessProfileImageState,
  get('isSaving'),
);

export const isBeforeBusinessProfileImageSaving = createSelector(
  beforeBusinessProfileImageState,
  get('isSaving'),
);

export const isAfterBusinessProfileImageSaving = createSelector(
  afterBusinessProfileImageState,
  get('isSaving'),
);

export const hasAvatarBusinessProfileImageError = createSelector(
  avatarBusinessProfileImageState,
  get('hasError'),
);

export const hasBeforeBusinessProfileImageError = createSelector(
  beforeBusinessProfileImageState,
  get('hasError'),
);

export const hasAfterBusinessProfileImageError = createSelector(
  afterBusinessProfileImageState,
  get('hasError'),
);
